<template>
    <div class="leaderboard-banner">
        <!-- Tabs: DAILY, WEEKLY, MONTHLY (pill control like Towers, dark purple bg) -->
        <div class="banner-tabs">
            <button
                v-for="tab in tabs"
                :key="tab.id"
                class="tab-btn"
                :class="{ 'tab-active': activeTab === tab.id }"
                @click="$emit('tab-change', tab.id)"
            >
                <div class="tab-inner">{{ tab.label }}</div>
            </button>
        </div>

        <!-- Podium section with cups and podium image -->
        <div class="podium-section">
            <!-- Left trophies -->
            <div class="cups-side cups-left">
                <img src="@/assets/img/leaderboard/cup.png" alt="" class="cup-img" />
            </div>

            <!-- Center: podium + top 3 users -->
            <div class="podium-center">
                <!-- Large rank labels in background -->
                <div class="rank-labels">
                    <span class="rank-label rank-2nd">2nd</span>
                    <span class="rank-label rank-1st">1st</span>
                    <span class="rank-label rank-3rd">3rd</span>
                </div>

                <!-- Top 3 users above podium -->
                <div class="podium-users">
                    <!-- 2nd place (left) -->
                    <div class="podium-user place-2nd">
                        <div class="user-avatar">
                            <AvatarImage :image="top3[1] && top3[1].user ? top3[1].user.avatar : ''" />
                        </div>
                        <div class="user-name">@{{ getUsername(top3[1]) }}</div>
                        <div class="user-score-pill">
                            <IconCoin />
                            <span>{{ formatScore(top3[1]) }}</span>
                        </div>
                    </div>
                    <!-- 1st place (center) -->
                    <div class="podium-user place-1st">
                        <div class="user-avatar">
                            <AvatarImage :image="top3[0] && top3[0].user ? top3[0].user.avatar : ''" />
                        </div>
                        <div class="user-name">@{{ getUsername(top3[0]) }}</div>
                        <div class="user-score-pill">
                            <IconCoin />
                            <span>{{ formatScore(top3[0]) }}</span>
                        </div>
                    </div>
                    <!-- 3rd place (right) -->
                    <div class="podium-user place-3rd">
                        <div class="user-avatar">
                            <AvatarImage :image="top3[2] && top3[2].user ? top3[2].user.avatar : ''" />
                        </div>
                        <div class="user-name">@{{ getUsername(top3[2]) }}</div>
                        <div class="user-score-pill">
                            <IconCoin />
                            <span>{{ formatScore(top3[2]) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Podium image -->
                <div class="podium-image-wrap">
                    <img src="@/assets/img/leaderboard/podium.png" alt="Podium" class="podium-image" />
                </div>
            </div>

            <!-- Right trophies -->
            <div class="cups-side cups-right">
                <img src="@/assets/img/leaderboard/cup.png" alt="" class="cup-img" />
            </div>
        </div>

        <!-- Title and "Leaderboard ends in" -->
        <div class="banner-footer">
            <h1 class="banner-title">Leaderboard</h1>
            <p class="ends-text">Leaderboard ends in</p>
        </div>
    </div>
</template>

<script>
import AvatarImage from '@/components/AvatarImage';
import IconCoin from '@/components/icons/IconCoin';

export default {
    name: 'LeaderboardBanner',
    components: { AvatarImage, IconCoin },
    props: {
        top3: {
            type: Array,
            default: () => [null, null, null]
        },
        activeTab: {
            type: String,
            default: 'daily'
        }
    },
    data() {
        return {
            tabs: [
                { id: 'daily', label: 'DAILY' },
                { id: 'weekly', label: 'WEEKLY' },
                { id: 'monthly', label: 'MONTHLY' }
            ]
        };
    },
    methods: {
        getUsername(winner) {
            if (!winner || !winner.user) return 'Anonymous';
            return winner.user.username || 'Anonymous';
        },
        formatScore(winner) {
            if (!winner) return '0';
            const value = winner.points != null ? winner.points : winner.prize || 0;
            return String(Math.floor(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
    }
};
</script>

<style scoped>
.leaderboard-banner {
    width: 100%;
    max-width: 900px;
    position: relative;
    padding: 0 0 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Tabs – pill control (like Towers), dark purple container */
.banner-tabs {
    width: 100%;
    max-width: 360px;
    display: flex;
    gap: 0;
    margin-bottom: 24px;
    position: relative;
    background: #2A1A47;
    border: 1px solid #4B2A7A;
    border-radius: 100px;
    padding: 2px;
}

.tab-btn {
    flex: 1;
    height: 40px;
    position: relative;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 1;
}

.tab-btn .tab-inner {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    background: transparent;
    border: none;
    border-radius: 100px;
    transition: all 0.3s ease;
    position: relative;
    z-index: 2;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.tab-btn.tab-active .tab-inner {
    color: #ffffff;
    background: linear-gradient(90deg, #D83DFF 0%, #6E39E0 100%);
    box-shadow: 0 2px 12px rgba(216, 61, 255, 0.35);
}

.tab-btn:hover:not(.tab-active) .tab-inner {
    color: #ffffff;
}

.tab-btn.tab-active:hover .tab-inner {
    color: #ffffff;
    background: linear-gradient(90deg, #D83DFF 0%, #6E39E0 100%);
}

/* Podium section */
.podium-section {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 0;
    width: 100%;
    position: relative;
    margin-bottom: 20px;
}

.cups-side {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    z-index: 1;
}

.cups-left {
    margin-right: -10px;
}

.cups-right {
    margin-left: -10px;
}

.cup-img {
    width: 64px;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 4px 12px rgba(212, 175, 55, 0.4));
}

.cups-right .cup-img {
    transform: scaleX(-1);
}

.podium-center {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
}

/* Large rank labels (background) */
.rank-labels {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    gap: 35px;
    align-items: flex-end;
    padding-bottom: 108px;
    pointer-events: none;
}

.rank-label {
    min-width: 100px;
    text-align: center;
    font-size: 42px;
    font-weight: 900;
    letter-spacing: 4px;
    color: rgba(255, 255, 255, 0.12);
    text-transform: uppercase;
}

.rank-2nd { order: 1; }
.rank-1st { order: 2; font-size: 48px; color: rgba(255, 215, 0, 0.18); }
.rank-3rd { order: 3; }

/* Users above podium */
.podium-users {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 35px;
    position: relative;
    z-index: 2;
}

.podium-user {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 100px;
}

.place-2nd { order: 1; }
.place-1st { order: 2; }
.place-3rd { order: 3; }

.user-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid rgba(255, 255, 255, 0.2);
    background: var(--bg-tertiary);
    margin-bottom: 6px;
}

.place-1st .user-avatar {
    width: 68px;
    height: 68px;
    border-color: #d4af37;
    box-shadow: 0 0 20px rgba(212, 175, 55, 0.4);
}

.user-avatar >>> .avatar-image,
.user-avatar >>> .avatar-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.user-name {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 6px;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-score-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(255, 215, 0, 0.25) 0%, rgba(212, 175, 55, 0.15) 100%);
    border: 1px solid rgba(255, 215, 0, 0.4);
}

.user-score-pill >>> svg,
.user-score-pill >>> img {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
}

.user-score-pill span {
    font-size: 14px;
    font-weight: 800;
    color: #ffd700;
}

/* Podium image */
.podium-image-wrap {
    width: 100%;
    max-width: 420px;
    line-height: 0;
}

.podium-image {
    width: 100%;
    height: auto;
    object-fit: contain;
}

/* Footer title */
.banner-footer {
    text-align: center;
}

.banner-title {
    font-size: 36px;
    font-weight: 900;
    letter-spacing: 6px;
    margin: 0 0 8px;
    text-transform: uppercase;
    background: linear-gradient(180deg, #ffd700 0%, #d4af37 50%, #b8860b 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.ends-text {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
}

@media only screen and (max-width: 768px) {
    .banner-tabs {
        margin-bottom: 16px;
        max-width: 100%;
    }

    .tab-btn {
        height: 36px;
    }

    .tab-btn .tab-inner {
        font-size: 12px;
    }

    .rank-label {
        font-size: 28px;
    }

    .rank-1st {
        font-size: 32px;
    }

    .podium-users {
        gap: 8px;
        margin-bottom: -10px;
    }

    .podium-user {
        min-width: 80px;
    }

    .user-avatar {
        width: 44px;
        height: 44px;
    }

    .place-1st .user-avatar {
        width: 52px;
        height: 52px;
    }

    .user-name {
        font-size: 11px;
    }

    .user-score-pill {
        padding: 4px 10px;
    }

    .user-score-pill span {
        font-size: 12px;
    }

    .cup-img {
        width: 48px;
    }

    .banner-title {
        font-size: 26px;
        letter-spacing: 3px;
    }
}
</style>
